import React, { useState, useEffect, Fragment } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { BsPlusLg as PlusLg, BsPlay as Play, BsPencil, BsPauseCircle as Pause, BsTrash } from 'react-icons/bs';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { ActionStyles } from '@/components/Admin/reusableStyles';
import { TableCSS } from '../styles/MembersStyled';
import { getTeamIdFromURL } from '@/_helpers';
import {
    createPresetAction,
    deletePresetAction,
    fetchPresetsAction,
    updatePresetAction,
} from '@/_redux/actions/ActionCallCenter';
import { connect } from 'react-redux';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import Swal from 'sweetalert2';
import AudioRecordModal from '@/components/Features/Common/ChatInputElements/AudioRecordModal';
import { uploadFile } from '@/_redux';
import { MdDelete } from 'react-icons/md';

const getSortedItems = (items = []) => items.sort((a, b) => a?.title?.localeCompare(b?.name));

const ItemRow = ({ sn, item, onEdit, onDelete, speakingPresetId, onPreview }) => (
    <tr key={item?._id}>
        <td>{sn}.</td>
        <td>{item?.title}</td>
        <td>{item?.introScript}</td>
        <td>
            <div className="d-flex gap-2">
                <ActionStyles>
                    <BootstrapToolTip text="Preview Intro Script">
                        {speakingPresetId === item?._id ? (
                            <Pause className="icon edit" onClick={() => onPreview(item.introScript, item._id)} />
                        ) : (
                            <Play className="icon " onClick={() => onPreview(item.introScript, item._id)} />
                        )}
                    </BootstrapToolTip>
                </ActionStyles>
                <ActionStyles>
                    <BootstrapToolTip text="Edit">
                        <BsPencil className="icon edit" onClick={() => onEdit(item)} />
                    </BootstrapToolTip>
                    <BootstrapToolTip text="Delete">
                        <BsTrash className="icon delete" onClick={() => onDelete(item)} />
                    </BootstrapToolTip>
                </ActionStyles>
            </div>
        </td>
    </tr>
);

const Component = ({ presets, createPresetAction, fetchPresetsAction, updatePresetAction, deletePresetAction }) => {
    const teamId = getTeamIdFromURL();
    const [isBusy, setIsBusy] = useState(false);
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [searchText, setSearchText] = useState('');

    const [showPresetModal, setShowPresetModal] = useState(false);
    const [editingPreset, setEditingPreset] = useState(null);
    const [presetData, setPresetData] = useState({
        title: '',
        introScript: '',
        aiPrompt: '',
        languageCode: 'en-US',
        sttVendor: '',
        ttsVendor: '',
    });

    const [isSpeaking, setIsSpeaking] = useState(false);
    const [speakingPresetId, setSpeakingPresetId] = useState(null);
    const [file, setFile] = useState(null);
    const getTeamPresets = async () => {
        try {
            const _items = presets;
            setItems(_items);
            setFilteredItems(getSortedItems([..._items]));
        } catch (error) {
            if (error?.name !== 'AbortError') toast.error(error.message);
        }
    };

    useEffect(() => {
        getTeamPresets();
    }, [presets]);

    const handleSearchChange = (e) => {
        const value = e.target.value.trim().toLowerCase();
        setSearchText(value);
        const _filtered = items.filter((p) => p.title?.toLowerCase().includes(value));
        setFilteredItems(getSortedItems(_filtered));
    };

    const stopSpeak = () => {
        if (window.speechSynthesis.speaking) window.speechSynthesis.cancel();
        setIsSpeaking(false);
        setSpeakingPresetId(null);
    };

    const previewSpeak = (text, presetId = null) => {
        try {
            if (!text) return toast.warn('No script to preview');

            if (isSpeaking && speakingPresetId === presetId) {
                stopSpeak();
                return;
            }

            stopSpeak();
            const utter = new SpeechSynthesisUtterance(text);
            utter.onend = stopSpeak;
            window.speechSynthesis.speak(utter);
            setIsSpeaking(true);
            setSpeakingPresetId(presetId);
        } catch (e) {
            toast.error(e.message);
        }
    };

    useEffect(
        () => () => {
            if (window.speechSynthesis.speaking) window.speechSynthesis.cancel();
        },
        []
    );

    const openCreateModal = () => {
        setEditingPreset(null);
        setPresetData({
            title: '',
            introScript: '',
            aiPrompt: '',
            languageCode: 'en-US',
            sttVendor: '',
            ttsVendor: '',
        });
        setShowPresetModal(true);
    };

    const openEditModal = (preset) => {
        setEditingPreset(preset);
        setPresetData({
            title: preset.title || '',
            introScript: preset.introScript || '',
            aiPrompt: preset.aiPrompt || '',
            languageCode: preset.languageCode || 'en-US',
            sttVendor: preset.sttVendor || '',
            ttsVendor: preset.ttsVendor || '',
            audioUrl: preset.audioUrl || '',
        });
        setShowPresetModal(true);
    };

    const handleSavePreset = async () => {
        let url;
        setIsBusy(true);
        const payload = {
            title: presetData.title,
            introScript: presetData.introScript,
            aiPrompt: presetData.aiPrompt,
            languageCode: presetData.languageCode,
            sttVendor: presetData.sttVendor,
            ttsVendor: presetData.ttsVendor,
            audioUrl: presetData.audioUrl,
        };
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            const response = await uploadFile(formData);
            if (response && response.length) {
                url = response[0].url;
            }
        }
        if (url) {
            payload.audioUrl = url;
        }
        try {
            if (editingPreset) {
                await updatePresetAction(editingPreset._id, payload);
            } else {
                payload.team = teamId;
                await createPresetAction(payload);
            }
            setShowPresetModal(false);
            setFile(null);
            await fetchPresetsAction(teamId);
        } catch (err) {
        } finally {
            setIsBusy(false);
        }
    };
    const handleDeletePreset = async (preset) => {
        try {
            const result = await Swal.fire({
                title: `Are you sure you want to delete ?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes',
            });

            if (result.isConfirmed) {
                await deletePresetAction(preset._id);
                await fetchPresetsAction(teamId);
            }
        } catch (err) {
            toast.error(err?.message || 'Failed to delete preset');
        }
    };

    const handleVoiceRecording = (audioFile) => {
        const maxFileSize = 48; // 50 MB limit
        if (audioFile && audioFile.size < maxFileSize * 1024 * 1024) {
            setFile(audioFile);
        } else {
            toast.error(`Audio file must be less than ${maxFileSize}mb`);
        }
    };

    return (
        <div className="presets-tab">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <input
                    className="input"
                    onChange={handleSearchChange}
                    value={searchText}
                    placeholder="Search presets..."
                    style={{
                        width: '250px',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        padding: '6px 10px',
                    }}
                />
                <Button variant="primary" onClick={openCreateModal}>
                    <PlusLg className="me-1" /> New Preset
                </Button>
            </div>

            {/* Table */}
            {!filteredItems ? (
                <div className="text-center py-5">
                    <VscLoading className="spin" fontSize="42" />
                </div>
            ) : (
                <div className="custom-card mt-3">
                    <TableCSS>
                        <thead>
                            <tr>
                                <th style={{ width: 10 }}>S.N.</th>
                                <th>Name</th>
                                <th>Intro Script</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.length > 0 ? (
                                filteredItems.map((item, index) => (
                                    <ItemRow
                                        sn={index + 1}
                                        key={item._id}
                                        item={item}
                                        onEdit={openEditModal}
                                        onDelete={handleDeletePreset}
                                        onPreview={previewSpeak}
                                        speakingPresetId={speakingPresetId}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="100">
                                        <div className="text-center py-4">
                                            <h5 className="text-muted mb-0">No Presets Found</h5>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </TableCSS>
                </div>
            )}

            {/* Modal */}
            <Modal show={showPresetModal} onHide={() => setShowPresetModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingPreset ? 'Edit Preset' : 'New Preset'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            value={presetData.title}
                            onChange={(e) => setPresetData({ ...presetData, title: e.target.value })}
                            placeholder="Enter preset title"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center justify-content-between">
                            Intro Script
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={presetData.introScript}
                            onChange={(e) => setPresetData({ ...presetData, introScript: e.target.value })}
                            placeholder="Hello, this is ..."
                        />
                        <Button variant="outline-secondary" onClick={() => previewSpeak(presetData.introScript)}>
                            <Play className="me-1" /> Preview Intro Script
                        </Button>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center justify-content-between">
                            Voice Recording (optional)
                        </Form.Label>
                    </Form.Group>
                    <div className="d-flex flex-row align-items-center">
                        {file || (presetData || {})?.audioUrl ? (
                            <Button
                                variant="outline-danger"
                                className="mb-2 ml-2"
                                onClick={() => {
                                    setFile(null);
                                    setPresetData({ ...presetData, audioUrl: undefined });
                                }}
                            >
                                <MdDelete fontSize="22px" />
                                Delete Voice
                            </Button>
                        ) : (
                            <AudioRecordModal
                                onRecordingComplete={handleVoiceRecording}
                                maxDuration={60} // 1 minutes max
                                fileName="call-center-audio-web.mp3"
                                compact={true}
                                buttonMode={true}
                            />
                        )}
                    </div>
                    {(file || (presetData || {})?.audioUrl) && (
                        <div>
                            <audio controls className="w-100">
                                <source
                                    src={file ? URL.createObjectURL(file) : (presetData || {})?.audioUrl}
                                    type="audio/mp3"
                                />
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPresetModal(false)} disabled={isBusy}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleSavePreset} disabled={isBusy}>
                        {isBusy && <VscLoading className="spin mr-2" />}
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
const mapStateToProps = (state) => ({
    presets: state.callCenter.presets || [],
});
const mapDispatchToProps = {
    fetchPresetsAction,
    createPresetAction,
    updatePresetAction,
    deletePresetAction,
};

const PresetsTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(PresetsTab);
